// Product page: fold the long description into a short intro (with "Read
// more"), a collapsed "… Highlights" section, and a visible lab-report (COA)
// link. Descriptions follow a common pattern: intro paragraphs, then a short
// "X Highlights" heading with a bullet list, then a "Download COA" link.
// Anything that doesn't fit the pattern is left untouched.
(() => {
  const isHeading = (el) => /^H[1-6]$/.test(el.tagName) ||
    (el.tagName === 'P' && el.textContent.trim().length <= 40 && el.querySelector('strong, b') &&
      el.textContent.trim() === (el.querySelector('strong, b') || {}).textContent?.trim());

  const enhance = (root) => {
    if (!root || root.dataset.ppFocus) return;
    root.dataset.ppFocus = 'true';
    const nodes = Array.from(root.children);
    if (nodes.length < 2) return;

    const coaNodes = nodes.filter((el) => Array.from(el.querySelectorAll('a')).some((a) => /coa|lab|certificate/i.test(a.textContent + a.href)));
    const coaLink = coaNodes.length ? coaNodes[0].querySelector('a') : null;
    const rest = nodes.filter((el) => !coaNodes.includes(el));

    // Drop a leading heading that just repeats the product title.
    const title = (document.querySelector('.product__title h1, .product__title .heading') || {}).textContent || '';
    if (rest[0] && /^H[1-6]$/.test(rest[0].tagName) && title && rest[0].textContent.trim() === title.trim()) rest.shift().remove();

    const firstList = rest.findIndex((el) => el.tagName === 'UL' || el.tagName === 'OL');
    let splitAt = firstList;
    if (firstList > 0 && isHeading(rest[firstList - 1])) splitAt = firstList - 1;
    if (splitAt <= 0) return; // no clear intro/highlights split

    const intro = rest.slice(0, splitAt);
    const details = rest.slice(splitAt);
    const headingEl = isHeading(details[0]) ? details.shift() : null;
    const label = headingEl ? headingEl.textContent.trim().replace(/:$/, '') : 'Highlights';

    const introWrap = document.createElement('div');
    introWrap.className = 'pp-pdp-desc__intro';
    intro.forEach((el) => introWrap.appendChild(el));

    const section = document.createElement('details');
    section.className = 'pp-pdp-desc__section';
    const summary = document.createElement('summary');
    summary.textContent = label;
    section.appendChild(summary);
    details.forEach((el) => section.appendChild(el));
    if (headingEl) headingEl.remove();

    root.prepend(introWrap);
    if (introWrap.scrollHeight > introWrap.clientHeight + 4) {
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'pp-pdp-desc__more';
      more.textContent = 'Read more';
      more.setAttribute('aria-expanded', 'false');
      more.addEventListener('click', () => {
        const open = introWrap.classList.toggle('is-expanded');
        more.textContent = open ? 'Show less' : 'Read more';
        more.setAttribute('aria-expanded', String(open));
      });
      introWrap.after(more);
    }
    const pdpDetails = root.closest('[data-pp-pdp-description]') && root.closest('details');
    if (pdpDetails) {
      // New layout: highlights become their own top-level section after "About".
      section.className = 'pp-pdp__section';
      const body = document.createElement('div');
      body.className = 'pp-pdp__section-body rte';
      Array.from(section.childNodes).filter((n) => n !== summary).forEach((n) => body.appendChild(n));
      section.appendChild(body);
      summary.textContent = /highlight/i.test(label) ? 'Flavor & effects' : label;
      pdpDetails.after(section);
    } else {
      root.appendChild(section);
    }

    // New layout shows the lab report in its own "Lab results" section.
    if (coaLink && root.closest('[data-pp-pdp-description]')) {
      coaNodes.forEach((el) => el.remove());
    } else if (coaLink) {
      coaNodes.forEach((el) => el.remove());
      coaLink.className = 'pp-pdp-desc__coa';
      coaLink.setAttribute('data-pp-coa', '');
      coaLink.textContent = 'View lab report (COA)';
      root.appendChild(coaLink);
    }
  };

  const run = () => document.querySelectorAll('.template-product .product__info .product__text.rte').forEach(enhance);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();

// Lab report (COA) popup: links marked [data-pp-coa] that point to a Google
// Drive file open Drive's embeddable preview in a dialog instead of a new tab.
// Anything else (or no <dialog> support) just follows the link as before.
(() => {
  if (window.ppCoaPopupReady) return;
  window.ppCoaPopupReady = true;

  // Embeddable URL for a Drive file (/file/d/ID) or folder (/drive/folders/ID)
  const driveEmbed = (href) => {
    try {
      const url = new URL(href, window.location.href);
      if (!/(^|\.)drive\.google\.com$/.test(url.hostname)) return null;
      const folder = url.pathname.match(/\/folders\/([\w-]+)/);
      if (folder) return `https://drive.google.com/embeddedfolderview?id=${encodeURIComponent(folder[1])}#list`;
      const file = url.pathname.match(/\/file\/d\/([\w-]+)/);
      const id = file ? file[1] : url.searchParams.get('id');
      return id ? `https://drive.google.com/file/d/${encodeURIComponent(id)}/preview` : null;
    } catch (error) {
      return null;
    }
  };

  let dialog = null;

  const build = () => {
    const d = document.createElement('dialog');
    d.className = 'pp-coa';
    d.setAttribute('aria-labelledby', 'PPCoaTitle');
    d.innerHTML = `
      <div class="pp-coa__panel">
        <div class="pp-coa__bar">
          <p class="pp-coa__title" id="PPCoaTitle">Lab report (COA)</p>
          <button type="button" class="pp-coa__close" data-pp-coa-close aria-label="Close">&times;</button>
        </div>
        <div class="pp-coa__frame">
          <p class="pp-coa__loading">Loading report…</p>
          <iframe title="Lab report" allow="autoplay" loading="lazy"></iframe>
        </div>
      </div>`;
    d.addEventListener('click', (event) => {
      if (event.target === d || event.target.closest('[data-pp-coa-close]')) d.close();
    });
    d.addEventListener('close', () => {
      document.documentElement.classList.remove('pp-coa-open');
      d.querySelector('iframe').removeAttribute('src');
    });
    document.body.appendChild(d);
    return d;
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-pp-coa]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const embed = driveEmbed(link.href);
    if (!embed || typeof HTMLDialogElement !== 'function') return;

    event.preventDefault();
    if (!dialog) dialog = build();
    const title = document.querySelector('.product__title h1, .product__title .heading');
    dialog.querySelector('.pp-coa__title').textContent = title ? `Lab report · ${title.textContent.trim()}` : 'Lab report (COA)';
    dialog.querySelector('iframe').src = embed;
    document.documentElement.classList.add('pp-coa-open');
    dialog.showModal();
  });
})();
